import { useState, useEffect } from 'react';
import { Star, StarOff, GripVertical, Loader2, Check, Eye, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import AdminLayout from '@/components/admin/AdminLayout';
import { productsAPI } from '@/lib/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (price) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);

export const AdminHome = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchProducts = async () => {
    try {
      const res = await productsAPI.getAll({ limit: 100 });
      const sorted = (res.data.products || res.data || []).sort((a, b) => (a.display_order || 999) - (b.display_order || 999));
      setProducts(sorted);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProducts(); }, []);

  const toggleFeatured = async (product) => {
    try {
      const token = localStorage.getItem('carvalho_token');
      await fetch(`${API_URL}/api/admin/products/${product.id}/featured`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ is_featured: !product.is_featured })
      });
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, is_featured: !p.is_featured } : p));
      toast.success(product.is_featured ? 'Removido dos destaques' : 'Adicionado aos destaques');
    } catch (e) { toast.error('Erro ao atualizar'); }
  };

  const moveProduct = async (index, direction) => {
    const newProducts = [...products];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newProducts.length) return;
    
    [newProducts[index], newProducts[targetIndex]] = [newProducts[targetIndex], newProducts[index]];
    newProducts.forEach((p, i) => p.display_order = i);
    setProducts(newProducts);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('carvalho_token');
      const orders = products.map((p, i) => ({ id: p.id, order: i }));
      await fetch(`${API_URL}/api/admin/products/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ orders })
      });
      toast.success('Ordem salva com sucesso!');
    } catch (e) { toast.error('Erro ao salvar ordem'); }
    finally { setSaving(false); }
  };

  const featuredProducts = products.filter(p => p.is_featured);

  return (
    <AdminLayout title="Gerenciar Home">
      <div className="space-y-6 max-w-5xl">
        {/* Featured Products Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Destaques da Home ({featuredProducts.length} selecionados)
              </CardTitle>
              <p className="text-xs text-slate-400">Marque os produtos que aparecem na secao de destaques</p>
            </div>
          </CardHeader>
          <CardContent>
            {featuredProducts.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Nenhum produto marcado como destaque. Use a estrela abaixo para selecionar.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {featuredProducts.map(p => (
                  <div key={p.id} className="bg-orange-50 border border-orange-200 rounded-lg p-2 flex items-center gap-2">
                    <img src={p.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-900 truncate">{p.name}</p>
                      <p className="text-xs text-orange-600 font-bold">{formatPrice(p.current_price)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* All Products with Ordering */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Todos os Produtos (Ordenacao)</CardTitle>
              <Button onClick={saveOrder} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="save-order-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Salvar Ordem
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
            ) : (
              <div className="space-y-1">
                {products.map((product, index) => (
                  <div
                    key={product.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      product.is_featured ? 'bg-orange-50 border-orange-200' : 'bg-white border-slate-100 hover:border-slate-200'
                    }`}
                    data-testid={`home-product-${product.id}`}
                  >
                    <span className="text-xs text-slate-400 font-mono w-6 text-center">{index + 1}</span>
                    
                    <div className="flex flex-col gap-0.5">
                      <Button variant="ghost" size="icon" className="w-6 h-6 text-slate-300 hover:text-slate-600" onClick={() => moveProduct(index, 'up')} disabled={index === 0}>
                        <ArrowUp className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="w-6 h-6 text-slate-300 hover:text-slate-600" onClick={() => moveProduct(index, 'down')} disabled={index === products.length - 1}>
                        <ArrowDown className="w-3 h-3" />
                      </Button>
                    </div>

                    <img src={product.image_url} alt="" className="w-12 h-12 rounded-lg object-cover bg-slate-100" />
                    
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{product.name}</p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="font-bold text-orange-600">{formatPrice(product.current_price)}</span>
                        <span className="bg-slate-100 px-1.5 py-0.5 rounded">{product.platform}</span>
                        {product.is_offer && <Badge variant="outline" className="text-[10px] border-orange-200 text-orange-600">Oferta</Badge>}
                      </div>
                    </div>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleFeatured(product)}
                      className={product.is_featured ? 'text-yellow-500 hover:text-yellow-600' : 'text-slate-300 hover:text-yellow-500'}
                      data-testid="toggle-featured-btn"
                    >
                      {product.is_featured ? <Star className="w-5 h-5 fill-yellow-400" /> : <StarOff className="w-5 h-5" />}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminHome;
